/**
 * Every sound is synthesized with WebAudio (no audio files): bell-like partials through a short echo,
 * so the app sounds like a singing bowl rather than a game. The context is created on the first
 * sound, always after a tap, which satisfies mobile autoplay rules.
 */
export type Cue = 'ui' | 'swell' | 'charge' | 'burst' | 'flip' | 'reveal';

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let muted = false;

const ensure = (): AudioContext | null => {
    if (muted) return null;
    try {
        if (!ctx) {
            const Ctor =
                window.AudioContext ||
                (
                    window as unknown as {
                        webkitAudioContext: typeof AudioContext;
                    }
                ).webkitAudioContext;
            if (!Ctor) return null;
            ctx = new Ctor();
            master = ctx.createGain();
            master.gain.value = 0.55;
            // A single feedback echo gives every tone a cave-like tail.
            const echo = ctx.createDelay(1);
            echo.delayTime.value = 0.28;
            const feedback = ctx.createGain();
            feedback.gain.value = 0.38;
            const wet = ctx.createGain();
            wet.gain.value = 0.35;
            master.connect(ctx.destination);
            master.connect(echo);
            echo.connect(feedback);
            feedback.connect(echo);
            echo.connect(wet);
            wet.connect(ctx.destination);
        }
        if (ctx.state === 'suspended') void ctx.resume();
        return ctx;
    } catch {
        return null;
    }
};

interface Tone {
    freq: number;
    freqEnd?: number;
    dur: number;
    type?: OscillatorType;
    gain?: number;
    delay?: number;
    attack?: number;
}

const tone = (c: AudioContext, o: Tone) => {
    const t0 = c.currentTime + (o.delay ?? 0);
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = o.type ?? 'sine';
    osc.frequency.setValueAtTime(o.freq, t0);
    if (o.freqEnd)
        osc.frequency.exponentialRampToValueAtTime(o.freqEnd, t0 + o.dur);
    const peak = o.gain ?? 0.2;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + (o.attack ?? 0.01));
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + o.dur);
    osc.connect(g);
    g.connect(master!);
    osc.start(t0);
    osc.stop(t0 + o.dur + 0.05);
};

/** A struck bell: a fundamental plus inharmonic partials that die away faster. */
const bell = (
    c: AudioContext,
    freq: number,
    delay = 0,
    gain = 0.16,
    dur = 1.6
) => {
    [1, 2.76, 5.4].forEach((m, i) =>
        tone(c, {
            freq: freq * m,
            dur: dur / (1 + i * 0.9),
            gain: gain / (1 + i * 1.4),
            delay,
        })
    );
};

// A minor pentatonic over A: dark and open, never "happy".
const SCALE = [
    220, 261.63, 293.66, 329.63, 392, 440, 523.25, 587.33, 659.25, 784,
];

const CUES: Record<Cue, (c: AudioContext) => void> = {
    ui: (c) => bell(c, 880, 0, 0.07, 0.5),
    swell: (c) => {
        tone(c, {
            freq: 110,
            dur: 4,
            gain: 0.12,
            attack: 1.6,
            type: 'triangle',
        });
        tone(c, { freq: 165, dur: 4, gain: 0.07, attack: 2, type: 'sine' });
    },
    charge: (c) => {
        // A rising drone and a quickening run of bells.
        tone(c, {
            freq: 80,
            freqEnd: 480,
            dur: 3.2,
            gain: 0.16,
            attack: 2.4,
            type: 'sawtooth',
        });
        tone(c, {
            freq: 160,
            freqEnd: 960,
            dur: 3.2,
            gain: 0.06,
            attack: 2.8,
            type: 'triangle',
        });
        for (let i = 0; i < 14; i++) {
            const at = 3.1 * (1 - Math.pow(1 - i / 14, 1.6));
            bell(c, SCALE[i % SCALE.length] * 2, at, 0.05 + i * 0.004, 0.9);
        }
    },
    burst: (c) => {
        tone(c, { freq: 55, freqEnd: 30, dur: 1.4, gain: 0.35, type: 'sine' });
        [0, 2, 4, 6, 8].forEach((n, i) =>
            bell(c, SCALE[n], i * 0.04, 0.12, 2.6)
        );
        [7, 8, 9, 8, 9, 7].forEach((n, i) =>
            bell(c, SCALE[n] * 2, 0.25 + i * 0.09, 0.06, 1.2)
        );
    },
    flip: (c) => {
        tone(c, {
            freq: 1800,
            freqEnd: 300,
            dur: 0.22,
            gain: 0.05,
            type: 'sawtooth',
        });
        bell(c, 660, 0.12, 0.1, 1.4);
    },
    reveal: (c) => {
        [0, 3, 5, 7, 9].forEach((n, i) => bell(c, SCALE[n], i * 0.11, 0.1, 2));
    },
};

export const sound = {
    get muted() {
        return muted;
    },
    setMuted(value: boolean) {
        muted = value;
        if (value && ctx) void ctx.suspend();
    },
    play(cue: Cue) {
        const c = ensure();
        if (c) CUES[cue](c);
    },
};
